import { useState, useCallback, useEffect } from "react";
import {
    Destination,
    ReconnectFormData,
    ReconnectErrorMessages,
    IntegrationLog,
} from "../DestinationModal.types";
import { reconnectDestination } from "helpers/destinations/reconnectDestination";
import SwalAlertComp from "components/SwalAlert";
import { enumAuthTypes } from "enums/lead/auth-types";
import { capitalizeFirstLetter } from "Constant/string";
import { Box, Skeleton, Stack } from "@mui/material";

interface UseDestinationReconnectOptions {
    destination: Destination;
    integrationLogs?: IntegrationLog[];
    isLoadingLogs?: boolean;
    onReconnectSuccess?: () => void;
    language?: any;
}

interface UseDestinationReconnectReturn {
    formData: ReconnectFormData;
    errorMessages: ReconnectErrorMessages;
    isReconnectLoading: boolean;
    buttonStatus: boolean;

    authData: any;
    isAuthLoading: boolean;
    slug: string;
    beExtra: any;

    setFormData: (data: ReconnectFormData) => void;
    setErrorMessages: (errors: ReconnectErrorMessages) => void;
    setIsReconnectLoading: (loading: boolean) => void;
    setIsAuthLoading: (loading: boolean) => void;
    setAuthData: (data: any) => void;
    setSlug: (slug: string) => void;
    setBeExtra: (extra: any) => void;
    handleFormChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleReconnect: (type?: string) => Promise<void>;
    validateForm: () => boolean;

    reconnectButtonLabel: () => React.ReactNode;
}

export const useDestinationReconnect = ({
    destination,
    integrationLogs = [],
    isLoadingLogs = false,
    onReconnectSuccess,
    language,
}: UseDestinationReconnectOptions): UseDestinationReconnectReturn => {
    const [formData, setFormData] = useState<ReconnectFormData>({
        pixel_id: destination?.pixel_id || "",
        token: destination?.access_token || "",
        ad_account_id: destination?.account_id || "",
    });

    const [errorMessages, setErrorMessages] = useState<ReconnectErrorMessages>({
        token: "",
        ad_account_id: "",
    });

    const [isAuthLoading, setIsAuthLoading] = useState(false);
    const [isReconnectLoading, setIsReconnectLoading] = useState(false);
    const [buttonStatus, setButtonStatus] = useState(
        destination?.reconnect?.reconnect_with_installation === true,
    );

    const [authData, setAuthData] = useState<any>(null);
    const [slug, setSlug] = useState<string>("");
    const [beExtra, setBeExtra] = useState<any>(null);

    useEffect(() => {
        if (destination?.reconnect?.reconnect_with_installation) {
            setFormData({
                pixel_id: destination?.pixel_id || "",
                token: destination?.access_token || "",
                ad_account_id: destination?.account_id || "",
            });
        } else {
            setFormData({
                pixel_id: destination?.pixel_id || "",
            });
        }
        setButtonStatus(
            destination?.reconnect?.reconnect_with_installation === true,
        );
    }, [destination]);

    useEffect(() => {
        setErrorMessages({
            token: "",
            ad_account_id: "",
        });
    }, [destination?.id]);

    const handleFormChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            setFormData((prev) => ({ ...prev, [name]: value }));
            setErrorMessages((prev) => ({
                ...prev,
                [name]: value ? "" : "Cannot be blank!",
            }));
        },
        [],
    );

    // Validate form
    const validateForm = useCallback((): boolean => {
        const errors: ReconnectErrorMessages = {};

        if (!formData.pixel_id) {
            errors.token =
                language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!";
        }

        if (!formData.token) {
            errors.token =
                language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!";
        }

        if (
            destination?.destination_type?.code === "pncapi" &&
            !formData.ad_account_id
        ) {
            errors.ad_account_id =
                language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!";
        }

        setErrorMessages(errors);
        return Object.keys(errors).length === 0;
    }, [formData, destination?.destination_type?.code, language]);

    const handleReconnect = useCallback(
        async (type: string = enumAuthTypes.INPUT) => {
            if (!validateForm()) {
                return;
            }

            setIsReconnectLoading(true);

            try {
                const reconnectData: any = {
                    token: formData.token,
                    type,
                };

                if (
                    destination?.destination_type?.code === "pncapi" &&
                    formData.ad_account_id
                ) {
                    reconnectData.ad_account_id = formData.ad_account_id;
                }

                const response = await reconnectDestination(
                    destination.id,
                    reconnectData,
                );

                if (response.status) {
                    SwalAlertComp({
                        title:
                            language?.delete_swal_modal?.success ?? "Success",
                        message: response.message,
                        icon: "success",
                        timer: 3000,
                    });
                    setFormData((prev) => ({ ...prev, token: "" }));
                    onReconnectSuccess?.();
                } else {
                    SwalAlertComp({
                        title: language?.delete_swal_modal?.failed ?? "Failed",
                        message: response.message && response.message,
                        icon: "error",
                    });
                }
            } catch (error) {
                console.error("Reconnect error:", error);
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: "An error occurred during reconnection",
                    icon: "error",
                });
            } finally {
                setIsReconnectLoading(false);
            }
        },
        [formData, destination, language, validateForm, onReconnectSuccess],
    );

    const reconnectButtonLabel = useCallback((): React.ReactNode => {
        if (isLoadingLogs) {
            return (
                <Stack width="100%">
                    <Skeleton width="40%" height={16} />
                    <Skeleton width="60%" height={16} />
                </Stack>
            );
        }

        const item = integrationLogs?.[integrationLogs?.length - 1];
        return !integrationLogs?.length ? null : (
            <Stack width="100%">
                <Box fontSize="12px !important" fontWeight="bold">
                    {item?.user_name}
                </Box>
                <Box fontSize="12px !important">{`
                    ${capitalizeFirstLetter(
                        item?.action === "create" ? "created" : "reconnected",
                    )} at: ${item?.created_at}
                `}</Box>
            </Stack>
        );
    }, [integrationLogs, isLoadingLogs]);

    return {
        formData,
        errorMessages,
        isReconnectLoading,
        buttonStatus,
        authData,
        slug,
        beExtra,

        setFormData,
        setErrorMessages,
        setIsReconnectLoading,
        setAuthData,
        setSlug,
        setBeExtra,
        handleFormChange,
        handleReconnect,
        validateForm,

        reconnectButtonLabel,
    };
};

export default useDestinationReconnect;
